"use client";

import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

export default function RegisterPage() {
  const router = useRouter();
  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    firstName: "",
    lastName: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const res = await fetch("/api/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    const data = await res.json();
    if (!res.ok) {
      setError(data?.error ?? "Registration failed");
      setLoading(false);
      return;
    }

    const loginRes = await signIn("credentials", {
      email: form.email,
      password: form.password,
      redirect: false,
    });
    setLoading(false);
    if (loginRes?.error) {
      setError("Registered, but sign-in failed. Try logging in manually.");
      return;
    }
    router.push("/");
    router.refresh();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-medium">Create account</h2>
        <p className="mt-1 text-sm text-[var(--muted-hi)]">Tar 30 sekunder.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" value={form.firstName} onChange={(v) => update("firstName", v)} />
        <Field label="Last name" value={form.lastName} onChange={(v) => update("lastName", v)} />
      </div>

      <Field label="Username" required value={form.username} onChange={(v) => update("username", v)} hint="3–32 tegn · bokstaver/tall/_-" />
      <Field label="Email" type="email" required value={form.email} onChange={(v) => update("email", v)} />
      <Field label="Password" type="password" required value={form.password} onChange={(v) => update("password", v)} hint="Minst 8 tegn" />

      {error && (
        <div className="rounded bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <button type="submit" disabled={loading} className="btn-primary w-full">
        {loading ? "Creating..." : "Create account"}
      </button>

      <p className="text-center text-sm text-[var(--muted)]">
        Har du konto?{" "}
        <Link href="/login" className="text-[var(--accent-hover)] hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="label-micro">{label}</div>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field"
      />
      {hint && <p className="text-xs text-[var(--muted)]">{hint}</p>}
    </div>
  );
}
